import React, { useState } from "react";
import Logo from "@/assets/Pitchster-logo.png";
import CustomInput from "@/components/CustomInput/CustomInput";
import { isValid } from "@/utils/validators";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Lock } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import TurnstileWidget from '@/components/captcha/TurnstileWidget'; 


const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const navigate = useNavigate();

  const { forgotPassword, forgotPasswordError } = useAuth();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email.trim()) {
      setError("Please enter your email");
      return;
    }
    if (!isValid("email", email)) {
      setError("Please enter a valid email");
      return;
    }
    if (!captchaToken) {
      toast.error("Please complete captcha verification");
      return;
    }

    setIsSubmitting(true);

    forgotPassword(
      { email ,captchaToken},
      {
        onSuccess: () => {
          toast.success(`Password reset link sent to your email.`);
          setTimeout(() => {
            setIsSubmitting(false);
            navigate("/login");
          }, 3000);
        },
        onError: () => {
          const msg =
            forgotPasswordError?.message || "Email verification failed.";
          toast.error(msg);
          setIsSubmitting(false);
        },
      },
    );
  };

  return (
    <div className="min-h-screen flex font-sans text-white bg-gradient-to-br from-[#0e002f] via-[#1d0340] to-[#0f001f]">
      <div className="w-full md:w-1/2 flex items-center justify-center px-5 py-6 bg-[#1a103f]">
        <div className="w-full max-w-sm">
          <div className="flex items-center justify-center space-x-6 mb-6">
            <img src={Logo} alt="Logo" className="w-40 h-15" />
          </div>

          <h2 className="text-center text-2xl font-bold">
            Reset Your Password
          </h2>
          <p className="text-center text-gray-400 text-sm mb-4">
            Enter your email to receive a reset link
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <CustomInput
              label="Email"
              name="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={(e) => {
                if (!e.target.value.trim()) setError("Please enter your email");
                else if (!isValid("email", e.target.value))
                  setError("Please enter a valid email");
                else setError("");
              }}
              error={error}
              required
            />

            <TurnstileWidget
              onVerify={(token) => setCaptchaToken(token)}
              onExpire={() => setCaptchaToken(null)}
              onError={() => setCaptchaToken(null)}
            />

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-3 rounded-full cursor-pointer transition font-semibold ${
                isSubmitting
                  ? "bg-gray-500 cursor-not-allowed"
                  : "bg-pink-600 hover:bg-pink-700"
              }`}
            >
              {isSubmitting ? "Sending..." : "Send Reset Link"}
            </button>

            <div className="text-center text-sm text-gray-300">
              <span
                className="hover:underline cursor-pointer"
                onClick={() => navigate("/login")}
              >
                Back to Login
              </span>
            </div>
          </form>
        </div>
      </div>

      <div className="hidden md:flex w-1/2 items-center justify-center bg-gradient-to-tr from-[#1f005c] to-[#2c005e] relative overflow-hidden">
        <div className="absolute w-[300px] h-[300px] bg-gradient-to-br from-pink-500 via-purple-600 to-blue-500 opacity-30 rounded-full blur-3xl animate-pulse"></div>
        <div className="text-center z-10 px-10">
          <div className="flex justify-center mb-10">
            <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center text-4xl text-white shadow-lg">
              <Lock size={42} className="text-white" />
            </div>
          </div>
          <p className="text-gray-300 text-md max-w-md mx-auto">
            We’ll send you a secure link to reset your password and get back to
            your account safely.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
